import { Component } from '@theme/component';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];
  #index = 0;
  #trigger;
  #abort;
  #touch;

  connectedCallback() {
    super.connectedCallback();
    this.#abort = new AbortController();
    const options = { signal: this.#abort.signal };
    const dialog = this.refs.dialog;
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.close();
    }, options);
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog || event.target === dialog.querySelector('.dialog-zoomed-gallery')) {
        this.close();
      } else if (event.target instanceof HTMLImageElement && event.target.closest('.dialog-zoomed-gallery') && matchMedia('(min-width: 750px)').matches) {
        this.refs.media[this.#index]?.classList.toggle('ls-photo-zoomed');
      }
    }, options);
    dialog.addEventListener('pointermove', (event) => {
      const item = this.refs.media[this.#index];
      if (!item?.classList.contains('ls-photo-zoomed')) return;
      const image = item.querySelector('img');
      const bounds = item.getBoundingClientRect();
      if (image && bounds.width && bounds.height) {
        image.style.transformOrigin = `${Math.max(0, Math.min(100, (event.clientX - bounds.left) / bounds.width * 100))}% ${Math.max(0, Math.min(100, (event.clientY - bounds.top) / bounds.height * 100))}%`;
      }
    }, options);
    dialog.addEventListener('touchstart', (event) => {
      this.#touch = event.touches.length === 1 ? { x: event.touches[0].clientX, y: event.touches[0].clientY } : null;
    }, { ...options, passive: true });
    dialog.addEventListener('touchmove', (event) => {
      if (event.touches.length !== 1) this.#touch = null;
    }, { ...options, passive: true });
    dialog.addEventListener('touchend', (event) => {
      if (!this.#touch || !event.changedTouches.length || event.touches.length) return;
      const dx = event.changedTouches[0].clientX - this.#touch.x;
      const dy = event.changedTouches[0].clientY - this.#touch.y;
      this.#touch = null;
      if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) dx < 0 ? this.next() : this.previous();
    }, { ...options, passive: true });
  }

  disconnectedCallback() {
    this.#abort?.abort();
    super.disconnectedCallback();
  }

  open(index, event) {
    event?.preventDefault();
    this.#trigger = document.activeElement;
    this.selectThumbnail(Number(index));
    if (!this.refs.dialog.open) this.refs.dialog.showModal();
    this.refs.dialog.querySelector('.dialog-zoomed-gallery__close-button')?.focus({ preventScroll: true });
  }

  selectThumbnail(index) {
    const media = this.refs.media;
    if (!media.length) return;
    this.#index = ((Number(index) || 0) % media.length + media.length) % media.length;
    media.forEach((item, i) => {
      item.hidden = i !== this.#index;
      item.setAttribute('aria-hidden', String(i !== this.#index));
      item.classList.remove('ls-photo-zoomed');
      item.querySelectorAll('video').forEach((video) => video.pause());
    });
    const current = this.refs.dialog.querySelector('[data-photo-current]');
    if (current) current.textContent = String(this.#index + 1);
    this.refs.thumbnails.querySelectorAll('button').forEach((button, i) => button.setAttribute('aria-selected', String(i === this.#index)));
    this.loadHighResolutionImage(media[this.#index]);
    this.dispatchEvent(new ZoomMediaSelectedEvent(this.#index));
  }

  loadHighResolutionImage(item) {
    if (!item?.classList.contains('product-media-container--image')) return;
    const image = item.querySelector('img.product-media__image');
    const url = image?.getAttribute('data_max_resolution');
    if (image && url && !image.dataset.lsHighRes) {
      image.dataset.lsHighRes = 'true';
      image.removeAttribute('srcset');
      image.src = url;
    }
  }

  next() { this.selectThumbnail(this.#index + 1); }
  previous() { this.selectThumbnail(this.#index - 1); }
  handleThumbnailClick(index) { this.selectThumbnail(Number(index)); }
  handleThumbnailPointerEnter(index) { this.loadHighResolutionImage(this.refs.media[index]); }

  handleKeyDown(event) {
    if (event.key === 'Escape') { event.preventDefault(); this.close(); }
    else if (event.key === 'ArrowRight') { event.preventDefault(); this.next(); }
    else if (event.key === 'ArrowLeft') { event.preventDefault(); this.previous(); }
    else if (event.key === 'Home') { event.preventDefault(); this.selectThumbnail(0); }
    else if (event.key === 'End') { event.preventDefault(); this.selectThumbnail(this.refs.media.length - 1); }
  }

  close() {
    this.refs.media.forEach((item) => {
      item.classList.remove('ls-photo-zoomed');
      item.querySelectorAll('video').forEach((video) => video.pause());
    });
    this.refs.dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
    if (this.#trigger instanceof HTMLElement && this.#trigger.isConnected) this.#trigger.focus({ preventScroll: true });
  }

  closeDialog() { this.close(); }
}
if (!customElements.get('zoom-dialog')) customElements.define('zoom-dialog', ZoomDialog);
